import {
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  Injector,
  NgModuleRef,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CustomerStore } from '../../../@state/customer.store';
import { WorkflowService } from '../../../services/workflow.service';
import { tap, takeUntil, first } from 'rxjs/operators';
import { Subject, combineLatest, from } from 'rxjs';

import { TableBuilder } from 'src/app/common/builder/gs.table.builder';
import { ColumnTypes, TableTypes } from 'src/app/common/entity/enums';
import { FormTypes } from 'src/app/common/entity/enum/formTypes';
import { CustomerBaseComponent } from '../../customerBase.component';
import {
  Item,
  Session,
} from 'src/app/common/entity/entities';
import { FormBuilder } from 'src/app/common/builder/gs.form.builder';
import { CustomerClient } from 'src/app/client/customer.client';
import { TranslateService } from '@ngx-translate/core';
import {
  ClientTypes,
  WorkflowExecutionTypes,
} from '../../../../../common/entity/enum/workflowExecutionStatuses';
import {
  Guid,
  scrollTo
} from '../../../../../common/utils/utils';
import { FormDropdownProperty } from '../../../../../common/entity/form/formDropdown.property';
import { FormInputSwitchProperty } from '../../../../../common/entity/form/formInputSwitch.property';
import { GSPlaceholder } from '../../../../../common/directives/placeholder';
import { FormDateProperty } from '../../../../../common/entity/form/formDate.property';
import { DatePipe } from '@angular/common';
import { BroadcastService } from '../../../../../common/client/broadcast.service';
import { NotificationEvents } from '../../../../../common/entity/enum/notificationEvents';
import { FormButtonProperty } from '../../../../../common/entity/form/formButton.property';
import { FormProperty } from '../../../../../common/entity/form/formProperty';
import {
  applyParamMetaFlags,
  getWorkflowDefinitions,
  handleExecutionConfigColumns,
  isParamReadonly,
} from '../workflow.fn';
import { FormTextboxProperty } from '../../../../../common/entity/form/formTextbox.property';
import { ValidationTypes } from 'src/app/common/entity/enum/validationTypes';
import { TableControl } from 'src/app/common/component/table.control';
import { FormEmailProperty } from 'src/app/common/entity/form/formEmail.property';
import { FormMultiselectProperty } from '../../../../../common/entity/form/formMultiselect.property';
import { FormUsersByRolesProperty } from '../../../../../common/entity/form/formUsersByRoles.property';
import { FormTimeProperty } from '../../../../../common/entity/form/formTime.property';
import { regExLib } from '../../../../../common/regEx/regExLib';
import { FormRow } from '../../../../../common/entity/form/formRow';
import { NotificationHelper } from '../../../../../common/helpers/notification.helper';

export enum WorkflowClientExecutionTypes {
  MANUAL = 2,
  SCHEDULED = 1,
  ADMIN = 4,
  WFINVOKE = 3,
  MANUALGENERIC = 5,
}

@Component({
  selector: 'workflow-definitions',
  templateUrl: './workflow-definitions.component.html',
  styleUrls: ['./workflow-definitions.component.css'],
})
export class WorkflowDefinitionsComponent
  extends CustomerBaseComponent
  implements OnInit
{
  destroy$ = new Subject<void>();
  TableTypes = TableTypes;
  FormTypes = FormTypes;
  showAddDefinition = false;
  showExecuteWorkflow = false;
  showAddExecutionConfiguration = false;
  workflowParameters: WorkflowParameters;
  taskParamItem: Item;

  executionProperties = [];
  selectedExecConfiguration: any;
  newWfDefinitionItem;
  definitionDetailsProperties: FormProperty[] = [];
  adminExecRows;

  @ViewChildren(GSPlaceholder)
  public placeholders: QueryList<GSPlaceholder>;
  @ViewChild('wfTable') wfTable: TableControl;

  @Output() dialogStateChanged = new EventEmitter<boolean>();

  workflowTaskProperties: Record<string, any> = {};

  // One business date for the whole run: when any task of the composition carries a
  // SharedAcrossTasks parameter, the Run dialog shows a single date field (instead of one per task)
  // and writes it into every day-reference parameter of every task at the start.
  readonly sharedDateItemKey = 'sharedRunBusinessDate';
  readonly sharedDatePropertyName = 'Shared_BusinessDate_wf_date';
  sharedDateProperties: Record<string, any> = {};
  private sharedRunDate = false;
  private runWorkflowClientId: number;
  // Parameters with an Api source (Meta.SourceType 'Api', e.g. the snapshot list), loaded from the
  // Web API once the parameter they depend on (Meta.DependsOn) has a value, and on every change of it.
  private apiSourceParams: ApiSourceParam[] = [];

  showDeleteWFDefinition: boolean = false;
  showDeleteWFExecConfig: boolean = false;


  get definitionDialogItem() {
  return {
    ...this.selectedWFDefinition,
    revertData: () => { }
  };
}

  get execConfigDialogItem() {
  return {
    ...this.selectedExecConfiguration?.item,
    revertData: () => { }
  };
}


  get addDefinitionTitle() {
    if (this.newWfDefinitionItem != null) return 'Add Workflow definition';
    else if (this.customerStore.selectedWFDefinition$ != null)
      return 'Edit Workflow definition';
  }

  get executeWorkflowTitle() {
    return 'Execute Admin Workflow definition';
  }

  get runWorkflowTitle() {
    return 'Run workflow';
  }

  get executionConfigTitle() {
    return 'Add execution config';
  }

  get wizardItem() {
    return this.selected.items.get('pdap_integration_customer_workflows');
  }

  get wfDefinitions() {
    return this.wizardItem?.items.get('wfDefinitions');
  }

  get selectedWFDefinition() {
    return this.wfDefinitions?.items.selected;
  }

  get processingButton(): FormButtonProperty {
    return this.execColumns?.find((p) => p.name == 'Processing');
  }

  get clientTypeProperty(): FormDropdownProperty {
    return this.definitionDetailsProperties?.find(
      (p) => p.name == 'ClientType'
    ) as FormDropdownProperty;
  }

  get functionTypeProperty(): FormDropdownProperty {
    return this.definitionDetailsProperties?.find(
      (p) => p.name == 'FunctionType'
    ) as FormDropdownProperty;
  }

  get descriptionProperty(): FormTextboxProperty {
    return this.definitionDetailsProperties?.find(
      (p) => p.name == 'Description'
    ) as FormTextboxProperty;
  }

  get activeProperty(): FormInputSwitchProperty {
    return this.definitionDetailsProperties?.find(
      (p) => p.name == 'Active'
    ) as FormInputSwitchProperty;
  }

  get localStorageKey_expandItems() {
    return `${this.customerStore.selected.Code}_expandedRowKeys`;
  }

  get localStorage_expandItems() {
    return this.customerStore?.selected?.Code && localStorage.getItem(this.localStorageKey_expandItems)
      ? JSON.parse(localStorage.getItem(this.localStorageKey_expandItems))
      : [];
  }

  get pageSize() {
    return this.wizardItem?.parameters['pageSize'] || 10;
  }

  expandedRowKeys;

  execColumns = [];

  constructor(
    public customerStore: CustomerStore,
    private workflowService: WorkflowService,
    private customerClient: CustomerClient,
    private translate: TranslateService,
    private resolver: ComponentFactoryResolver,
    private datepipe: DatePipe,
    private broadcastService: BroadcastService
  ) {
    super();
    this.definitionDetailsProperties = FormBuilder.build(
      FormTypes.Workflow_Definitions_Details,
      this.translate
    );
    this.execColumns = TableBuilder.build(
      TableTypes.Workflow_Definitions_Configuration
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.taskParamItem = new Item(null, {});

    this.setDisableParameterRef('FK_WorkflowClientExecutionType');
    this.setDisableParameterRef('FK_TimeZone');
    this.setDisableParameterRef('FK_ScheduleFrequency');
    this.setDisableParameterRef('MaxAttempts');
    this.setDisableParameterRef('WaitBetweenAttempts');
    this.setDisableParameterRef('ScheduleFrequencyValue');
    this.setDisableParameterRef('FunctionType');
    this.setDisableParameterRef('Active');
    this.setDisableParameterRef('DoNotExecuteBefore');

    const functionTypeCol = this.execColumns.find(
      (x) => x.name == 'FunctionType'
    );
    functionTypeCol.options = this.getFunctionTypeOptions();

    setTimeout(() => {
      this.handleOnSetWizardItem();
      this.handleOnClearWizardItem();
    }, 100);

    this.menuItem?.items.onSelected.subscribe((item) => {
      if (this.wizardItem) this.loadWFDefinitions();
      else
        item.onSetItem$.pipe().subscribe((i) => {
          if (i?.key == 'pdap_integration_customer_workflows') {
            setTimeout(() => {
              this.loadWFDefinitions();
            }, 100);
            this.handleOnSetWizardItem();
            this.handleOnClearWizardItem();
          }
        });
    }, this);

    this.executionProperties = FormBuilder.build(
      FormTypes.Workflow_Definitions_Execution_Configuration,
      this.translate
    );

    this.functionTypeProperty.options = this.getFunctionTypeOptions();

    this.setOptions();
    if (this.customerStore?.selected?.Code) {
      this.loadWFDefinitions();
    } else if (this.customerStore.ids.length) {
      this.customerStore.selectCustomer(this.customerStore.ids[0]);
      this.loadWFDefinitions();
    } else if (this.menuItem?.items?.selected?.code) {
      this.customerStore.selectCustomer(this.customerStore.ids[0]);
      this.loadWFDefinitions();
    }
  }

  handleOnClearWizardItem() {
    this.wizardItem.items.onClearItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.expandedRowKeys = undefined;
      });
  }

  handleOnSetWizardItem() {
    this.wizardItem.onSetItem$
      .pipe(
        tap((i: Item) => {
          if (i?.key == 'wfDefinitions') {
            this.expandedRowKeys = {};
            i.onSetItem$
              .pipe(
                tap((wfItem: Item) => {
                  if (wfItem) {
                    if (!wfItem?.parameters.contains('column'))
                      wfItem.parameters['column'] = {};
                    if (!wfItem?.parameters.column['Delete'])
                      wfItem.parameters.column['Delete'] = {};

                    wfItem.parameters.column['Delete'].class =
                      'background-color-error';

                    if (
                      wfItem?.items
                        .get('Configurations')
                        ?.items.values.find(
                          (x) =>
                            x.data.FK_WorkflowClientExecutionType ==
                            WorkflowClientExecutionTypes.MANUALGENERIC
                            ||
                            (
                              x.data.FK_WorkflowClientExecutionType == WorkflowClientExecutionTypes.SCHEDULED
                              && x.data.CreatedBySystem == true
                            )
                        ) != undefined
                    )
                      wfItem.parameters.column['Delete'].visible = false;
                    else wfItem.parameters.column['Delete'].visible = true;
                  }
                  if (
                    wfItem?.data?.Configurations?.find(
                      (c) =>
                        c.FK_WorkflowClientExecutionType ==
                        WorkflowClientExecutionTypes.MANUALGENERIC
                        ||
                        (
                          c.FK_WorkflowClientExecutionType == WorkflowClientExecutionTypes.SCHEDULED
                          && c.CreatedBySystem == true
                        )
                    ) !== undefined
                  ) {
                    wfItem.parameters['workflowDef_active'] = true;
                    wfItem.parameters['workflowDef_details'] = true;
                    wfItem.parameters['acceptChanges'] = true;
                    wfItem.parameters['column'] = { Code: { hide: true } };
                    if (!wfItem?.parameters.contains('column'))
                      wfItem.parameters['column'] = {};
                    if (!wfItem?.parameters.column['Delete'])
                      wfItem.parameters.column['Delete'] = {};
                    wfItem.parameters.column['Delete'].visible = false;
                    wfItem.readonly = true;
                  }
                  if (wfItem && this.localStorage_expandItems?.length) {
                    const index = this.localStorage_expandItems.indexOf(
                      wfItem.data.PK_ID?.toString()
                    );
                    if (index !== -1) this.addExpandedKey(wfItem);
                  }
                  if (wfItem && wfItem.data?.Expanded)
                    this.addExpandedKey(wfItem);
                  if (this.wfTable &&
                    localStorage.getItem(
                      `${this.customerStore.selected.Code}_WF_currentPage`
                    )
                  )
                    this.wfTable.navigateToPage(
                      +localStorage.getItem(
                        `${this.customerStore.selected.Code}_WF_currentPage`
                      )
                    );
                  this.focusLastEditedItem(wfItem);
                }),
                takeUntil(this.destroy$)
              )
              .subscribe();
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  loadWFDefinitions() {
    const data = this.wizardItem.data;
    if (data && !this.wfDefinitions?.items?.values?.length)
      getWorkflowDefinitions(
        this.selected.code,
        this.workflowService,
        this.customerStore,
        this.wizardItem
      );
  }

  getFunctionTypeOptions() {
    return [
      {
        label: 'Select option',
        value: null,
      },
      {
        label: 'PAYROLL EXPORT',
        value: 'PAYROLL_EXPORT',
      },
      {
        label: 'SYNC',
        value: 'SYNC',
      },
      {
        label: 'CLOSE DATE',
        value: 'CLOSE_DATE',
      },
      {
        label: 'LOAD CARD',
        value: 'LOAD_CARD',
      },
      {
        label: 'DATA EXPORT',
        value: 'DATA_EXPORT',
      },
    ];
  }

  setDisableParameterRef(colName) {
    const execTypeCol = this.execColumns.find((c) => c.name === colName);
    execTypeCol.disabledParameterReference = `${colName}_readOnly`;
  }

  setOptions() {
    const executionTypes$ =
      this.workflowService.GetWorkflowClientExecutionType();
    const scheduleFrequencyTypes$ =
      this.workflowService.GetScheduleFrequencyType();
    const timeZone$ = from(this.customerClient.getTimeZones());
    combineLatest([executionTypes$, scheduleFrequencyTypes$, timeZone$])
      .pipe(
        tap(([execTypes, scheduledTypes, timeZone]) => {
          this.handleOptions(
            this.execColumns,
            'FK_WorkflowClientExecutionType',
            execTypes.result,
            'Code'
          );
          this.handleOptions(
            this.execColumns,
            'FK_ScheduleFrequency',
            scheduledTypes.result,
            'Name'
          );
          this.handleOptions(
            this.execColumns,
            'FK_TimeZone',
            timeZone.result,
            'DisplayName',
            'ID'
          );
          this.handleOptions(
            this.executionProperties,
            'TimeZone',
            timeZone.result,
            'DisplayName',
            'ID'
          );
        })
      )
      .subscribe();
  }

  handleOptions(source, colName, data, label, value = 'PK_ID') {
    const col = source.find((x) => x.name == colName);
    if (!col || !data?.length) return;

    col.options = [
      ...[{ value: null, label: 'Please Select Options', isDisabled: false }],
      ...data.map((x) => {
        return {
          value: x[value],
          label: x[label],
          data: x,
          isDisabled:
            x[label] == 'WFInvoke' || x[label] == 'ManualGeneric'
              ? true
              : false,
        };
      }),
    ];
  }

  override loadData(item): void {
    if (!this.item) this.selected.setItem(null, this.itemName);
  }

  rowAction(e) {
    if (e.columnName === 'Details') {
      e.item.select();
      this.customerStore.selectWorkflowDefinition(e.row);
      this.showAddDefinition = true;
      this.dialogStateChanged.emit(true);
      this.clientTypeProperty.readonly = true;
      this.handelDetailsProps(false);
    } else if (e.columnName == 'Delete') {
      e.item.select();
      this.customerStore.selectWorkflowDefinition(e.row);
      //this.selectedWFDefinition.data['Deleted'] = true;
      this.showDeleteWFDefinition = true;
      this.dialogStateChanged.emit(true);
    }

    if (e.action === 'expand') {
      this.selectWfDefinition(e.item);
      this.handleExpandItems(e.item.data, e.action);
      this.customerStore.selectWorkflowDefinition(e.row);
      const customerCode = this.customerStore.selected.Code;
      if (e.row.PK_ID < 0) {
        if (!e.row.Tasks) {
          this.customerStore.setCustomerWorkflowTasks(
            [],
            e.row.PK_ID,
            customerCode
          );
        }
        this.handleConfigurationActions(e.item);
      } else if (e.row.PK_ID > 0) {
        if (!e.row.Tasks?.length) {
          let manualGenericCfg = e.row?.Configurations?.find(
            (c) =>
              c.FK_WorkflowClientExecutionType ==
              WorkflowClientExecutionTypes.MANUALGENERIC
          );
          let isManualGeneric = manualGenericCfg != undefined;
          let gsFunctionType = manualGenericCfg?.FunctionType ?? '';

          this.workflowService
            .getWorkflowDefinitionTasks(
              e.row.PK_ID,
              customerCode,
              isManualGeneric,
              gsFunctionType
            )
            .subscribe((data) => {
              e.item.parameters['acceptChanges'] = true;
              e.item.data = { ...e.item.data, ...{ Tasks: data.result } };
              const wfDefinitionItem =
                this.wizardItem.items.get('wfDefinitions');
              const index = wfDefinitionItem.items.values.indexOf(e.item);
              const wfDefinitions = this.wizardItem.data.wfDefinitions;
              wfDefinitions.splice(index, 1, e.item.data);
              //update original data for wf definitions item
              wfDefinitionItem.data = wfDefinitions;
              if (!wfDefinitionItem.isDirty) wfDefinitionItem.acceptChanges();
              //update original data for wizard item
              wfDefinitions.forEach((wfDefinition) => {
                wfDefinition.Configurations.forEach((configuration) => {
                  if (!!configuration.DoNotExecuteBefore) {
                    configuration.DoNotExecuteBefore = new Date(configuration.DoNotExecuteBefore);
                  }
                })
              });
              this.wizardItem.data = {
                ...this.wizardItem.data,
                ...{
                  wfDefinitions,
                },
              };

              this.customerStore.setCustomerWorkflowTasks(
                data.result,
                e.row.PK_ID,
                customerCode
              );
              if (!this.wizardItem.isDirty) this.wizardItem.acceptChanges();
              if (!this.wfDefinitions.isDirty)
                this.wfDefinitions.acceptChanges();

              this.handleConfigurationActions(e.item);
            });
        } else this.handleConfigurationActions(e.item);
      }
    } else if (e.action === 'collapse') {
      this.handleExpandItems(e.row, e.action);
    }
  }

  handleConfigurationActions(item: Item) {
    if ((item?.items?.values.length ?? 0) != 0) {
      item.items.values
        .find((i) => i.key == 'Configurations')
        .items.values.forEach((cfg: Item) => {
          cfg.parameters['FK_WorkflowClientExecutionType_readOnly'] =
            cfg.data.PK_ID > 0;
          handleExecutionConfigColumns(
            cfg,
            cfg.data.FK_WorkflowClientExecutionType,
            item
          );

          cfg.parameters['column'] = {
            Run: {
              name: 'Run',
              description: 'Run',
              visible:
                cfg.data.FK_WorkflowClientExecutionType ==
                WorkflowExecutionTypes.Admin && cfg.data.Active,
              disabled: this.customerStore.selectedWFDefinition.Tasks.length == 0,
              icon: 'fas fa-play-circle',
              type: ColumnTypes.Button,
            },
            Processing: {
              name: 'Processing',
              description: '',
              visible: false,
              icon: 'fa fa-spinner fa-spin',
              type: ColumnTypes.Button,
            },
            Active: {
              name: 'Active',
              description: 'Active',
              disabled:
                cfg.data.FK_WorkflowClientExecutionType ==
                WorkflowExecutionTypes.ManualGeneric ||
                (
                  cfg.data.FK_WorkflowClientExecutionType == WorkflowExecutionTypes.Scheduled
                  && cfg.data.CreatedBySystem == true
                ),
              type: ColumnTypes.BoolSwitch,
            },
          };

          if (!cfg.parameters.contains('column')) cfg.parameters['column'] = {};
          if (!cfg.parameters.column['Delete'])
            cfg.parameters.column['Delete'] = {};

          cfg.parameters.column['Delete'].class = 'background-color-error';

          if (
            cfg.data.FK_WorkflowClientExecutionType ==
            WorkflowExecutionTypes.ManualGeneric
            ||
            (
              cfg.data.FK_WorkflowClientExecutionType == WorkflowExecutionTypes.Scheduled
              && cfg.data.CreatedBySystem == true
            )
          )
            cfg.parameters.column['Delete'].visible = false;
          else cfg.parameters.column['Delete'].visible = true;

          if (
            cfg.data.FK_WorkflowClientExecutionType ==
            WorkflowExecutionTypes.ManualGeneric
            ||
            (
              cfg.data.FK_WorkflowClientExecutionType == WorkflowExecutionTypes.Scheduled
              && cfg.data.CreatedBySystem == true
            )
          ) {
            cfg.acceptChanges();
            cfg.readonly = true;

            this.wizardItem.reCheckDirty();
            if (!this.wizardItem.isDirty) this.wizardItem.acceptChanges();
          }
        });
    }
  }

  handelDetailsProps(visible) {
    this.definitionDetailsProperties
      .filter((x) => ['hasAdmin', 'hasManual'].includes(x.name))
      .forEach((prop) => (prop.visible = visible));
  }

  change(e) {
    if (e.columnName === 'Active') {
      this.customerStore.selectWorkflowDefinition(e.row);
      this.customerStore.updateCustomerWFDefinition(e.row);
      this.wizardItem.reCheckDirty();
    }
  }

  add(e) {
    this.newWfDefinitionItem = new Item(null, { ClientType: undefined });
    this.showAddDefinition = true;
    this.dialogStateChanged.emit(true);
    this.handelDetailsProps(true);
    this.clientTypeProperty.readonly = false;
  }

  applyDefinition(e) {
    if (!e?.item) return;
    if (e?.item.data.PK_ID === undefined) {
      const colId = +localStorage.getItem('wfDefinitionId') - 1;
      localStorage.setItem('wfDefinitionId', colId.toString());
      const data = { ...e.item.data, ...{ PK_ID: colId, Tasks: [] } };
      this.customerStore.addCustomerWFDefinition(data);
      this.customerStore.selectWorkflowDefinition(data);
      if (e.item.data.hasAdmin) {
        const execId = this.generateId('executionConfigId');
        const data = {
          PK_ID: execId,
          MaxAttempts: 1,
          FK_WorkflowClientExecutionType: WorkflowClientExecutionTypes.ADMIN,
          FK_TimeZone: this.getClientTimeZone(e.item),
          Active: true,
        };
        this.customerStore.addCustomerWFDefinitionExecutionConfig(data);
      }
      if (e.item.data.hasManual) {
        const data = {
          PK_ID: this.generateId('executionConfigId'),
          MaxAttempts: 1,
          FK_WorkflowClientExecutionType: WorkflowClientExecutionTypes.MANUAL,
          FK_TimeZone: this.getClientTimeZone(e.item),
          FunctionType: e.item.data.FunctionType,
        };
        this.customerStore.addCustomerWFDefinitionExecutionConfig(data);
      }
      setTimeout(() => {
        const execItem = this.wfDefinitions.items.values.find(
          (i) => i.data.PK_ID == colId
        );
        const confItem = execItem.items.get('Configurations');
        confItem?.onSetItem$
          .pipe(
            tap((i: Item) => {
              confItem.items.values.forEach((child) => {
                handleExecutionConfigColumns(
                  child,
                  child.data.FK_WorkflowClientExecutionType,
                  execItem
                );
              });
            }),
            takeUntil(this.destroy$)
          )
          .subscribe();
      }, 100);
    } else {
      this.customerStore.updateCustomerWFDefinition(e.item.data, true);
      this.customerStore.resetWorkflowDefinition();
    }
    this.updateExpandItems(e.item.original?.PK_ID, e.item.data?.PK_ID);
    delete this.newWfDefinitionItem;
    this.showAddDefinition = false;
    this.dialogStateChanged.emit(false);
    this.functionTypeProperty.visible = this.functionTypeProperty.validate =
      false;
    this.wizardItem.reCheckDirty();
  }

  closeDefinition(e) {
    this.dialogStateChanged.emit(false);
    this.customerStore.resetWorkflowDefinition();
    delete this.newWfDefinitionItem;
  }

  executeWorkflowDefinition(e) {
    this.workflowParameters.workflowTaskParameters =
      new Array<WorkflowTaskParameters>();

    // the single date of the run goes, as an absolute date, into every day-reference
    // parameter of every task; the date item itself is not a task
    const sharedDate = this.sharedRunDate
      ? toIsoDate(e.item.items.get(this.sharedDateItemKey)?.data[this.sharedDatePropertyName], this.datepipe)
      : null;

    e.item.items.values.forEach((item) => {
      if (item.parameters['sharedRunDate']) return;

      if (sharedDate)
        Object.keys(item.data)
          .filter((k) => k.endsWith('_wf_date'))
          .forEach((k) => (item.data[k] = sharedDate));

      let taskParams = new WorkflowTaskParameters();
      taskParams.taskSequence = item.parameters.workflowTaskSequence;

      taskParams.parameters = item.data;

      // override for date properties
      if (
        item.data[
          `${item.parameters.workflowTaskSequence}_BusinessDate_wf_date`
        ]
      ) {
        taskParams.parameters['BusinessDate'] =
          item.data[
            `${item.parameters.workflowTaskSequence}_BusinessDate_wf_date`
          ];
      }

      if (
        item.data[
          `${item.parameters.workflowTaskSequence}_BusinessDateTo_wf_date`
        ]
      ) {
        taskParams.parameters['BusinessDateTo'] =
          item.data[
            `${item.parameters.workflowTaskSequence}_BusinessDateTo_wf_date`
          ];
      }

      if (
        taskParams.parameters.hasOwnProperty(
          `${item.parameters.workflowTaskSequence}_BusinessDate_wf_date`
        )
      )
        delete taskParams.parameters[
          `${item.parameters.workflowTaskSequence}_BusinessDate_wf_date`
        ];

      if (
        taskParams.parameters.hasOwnProperty(
          `${item.parameters.workflowTaskSequence}_BusinessDateTo_wf_date`
        )
      )
        delete taskParams.parameters[
          `${item.parameters.workflowTaskSequence}_BusinessDateTo_wf_date`
        ];

      Object.entries(item.data)
        .filter(
          ([k, v]) =>
            !k.endsWith('_wf_date') &&
            k.startsWith(`${item.parameters.workflowTaskSequence}_`)
        )
        .forEach((property) => {
          let propertyName = property[0];
          let propertyValue = property[1];
          let originalPropertyName = propertyName.replace(
            `${item.parameters.workflowTaskSequence}_`,
            ''
          );
          if (taskParams.parameters.hasOwnProperty(originalPropertyName)) {
            taskParams.parameters[originalPropertyName] = propertyValue;
            delete taskParams.parameters[propertyName];
          }
        });

      if (
        item.data.DeletePrevious !== undefined ||
        item.data.Generate !== undefined ||
        item.data.Send !== undefined
      ) {
        taskParams.parameters['Actions'] = {
          DeletePrevious: item.data.DeletePrevious ?? false,
          Generate: item.data.Generate ?? false,
          Send: item.data.Send ?? false,
        };

        if (taskParams.parameters.hasOwnProperty('DeletePrevious'))
          delete taskParams.parameters.DeletePrevious;

        if (taskParams.parameters.hasOwnProperty('Generate'))
          delete taskParams.parameters.Generate;

        if (taskParams.parameters.hasOwnProperty('Send'))
          delete taskParams.parameters.Send;
      }

      if (
        item.data.LoadCommission !== undefined ||
        item.data.LoadTips !== undefined ||
        item.data.LoadWages !== undefined ||
        item.data.LoadWageMakeUp !== undefined
      ) {
        taskParams.parameters['LoadCategory'] = {
          LoadTips: item.data.LoadTips ?? false,
          LoadWages: item.data.LoadWages ?? false,
          LoadCommission: item.data.LoadCommission ?? false,
          LoadWageMakeUp: item.data.LoadWageMakeUp ?? false,
        };

        if (taskParams.parameters.hasOwnProperty('LoadTips'))
          delete taskParams.parameters.LoadTips;

        if (taskParams.parameters.hasOwnProperty('LoadWages'))
          delete taskParams.parameters.LoadWages;

        if (taskParams.parameters.hasOwnProperty('LoadCommission'))
          delete taskParams.parameters.LoadCommission;

        if (taskParams.parameters.hasOwnProperty('LoadWageMakeUp'))
          delete taskParams.parameters.LoadWageMakeUp;
      }

      if (
        item.data.RegularLoad !== undefined ||
        item.data.DelayedLoad !== undefined ||
        item.data.AutoLoad !== undefined
      ) {
        taskParams.parameters['LoadType'] = {
          RegularLoad: item.data.RegularLoad ?? false,
          DelayedLoad: item.data.DelayedLoad ?? false,
          AutoLoad: item.data.AutoLoad ?? false,
        };

        if (taskParams.parameters.hasOwnProperty('RegularLoad'))
          delete taskParams.parameters.RegularLoad;

        if (taskParams.parameters.hasOwnProperty('DelayedLoad'))
          delete taskParams.parameters.DelayedLoad;

        if (taskParams.parameters.hasOwnProperty('AutoLoad'))
          delete taskParams.parameters.AutoLoad;
      }

      this.workflowParameters.workflowTaskParameters.push(taskParams);
    });

    this.subscribe(
      this.workflowParameters.processID,
      this.workflowParameters.clientCode
    );

    this.workflowService
      .executeWorkflow(this.workflowParameters)
      .subscribe(() => {
        this.selectedExecConfiguration.item.parameters.column.Run.disabled =
          true;
        this.selectedExecConfiguration.item.parameters.column.Processing.visible =
          true;
        this.workflowParameters = new WorkflowParameters();
        this.taskParamItem = new Item(null, {});
      });

    this.resetRunState();
    this.showExecuteWorkflow = false;
    this.dialogStateChanged.emit(false);
  }

  /** Whether a task of the Run form shows at least one parameter (rows of FormRow). */
  hasVisibleProperties(rows: any[]): boolean {
    // same condition as the template's *ngIf="property.visible"
    return (rows ?? []).some((row) => (row.properties ?? []).some((p) => !!p.visible));
  }

  private resetRunState() {
    this.sharedRunDate = false;
    this.sharedDateProperties = {};
    this.apiSourceParams = [];
  }

  closeExecuteWorkflowDefinition(e) {
    this.dialogStateChanged.emit(false);
    this.resetRunState();
    this.taskParamItem = new Item(null, {});
    this.customerStore.resetWorkflowDefinition();
  }

  configAction(e, parent) {
    if (e.columnName === 'Details') {
      this.customerStore.selectWorkflowDefinition(parent.data);
      this.customerStore.selectWorkflowDefinitionExecutionConfiguration(e.row);
      this.showAddExecutionConfiguration = true;
      this.dialogStateChanged.emit(true);
    } else if (e.columnName === 'Run') {
      this.selectedExecConfiguration = e;
      this.workflowParameters = new WorkflowParameters();
      this.workflowParameters.workflowClientExecutionCfgID = e.row.PK_ID;
      this.workflowParameters.executionType = WorkflowExecutionTypes.Admin;
      this.workflowParameters.gSFunctionType = e.row.FunctionType;
      this.workflowParameters.gSClientType =
        parent.data.ClientType == 'Customer' ? 2 : 1;
      this.workflowParameters.processID = Guid.newGuid().toString();
      this.workflowParameters.userFullName = Session.userName;
      this.workflowParameters.userCode = Session.userCode;
      this.workflowParameters.clientCode =
        parent.data.ClientType == 'Customer'
          ? this.customerStore.selected.Code
          : this.selected.data.CompanyGroup.Code;

      let taskParams: Record<string, any> = {};
      // every run starts from a fresh item: after a failed start the previous one (with its task and
      // shared date items) would otherwise be reused and its items added again under the same keys
      this.taskParamItem = new Item(null, {});
      this.showExecuteWorkflow = true;
      this.dialogStateChanged.emit(true);

      const activeTasks = this.customerStore.activeWF.definitions
        .find(
          (d) =>
            d.PK_ID == parent.data.PK_ID &&
            d.Description == parent.data.Description
        )
        .Tasks.filter((t) => t.Active == true);

      this.sharedRunDate = activeTasks.some((t) => hasSharedAcrossTasksParam(t.WorkflowTask.Params));
      this.runWorkflowClientId = parent.data.PK_ID;
      this.apiSourceParams = [];
      this.sharedDateProperties = this.sharedRunDate ? this.buildSharedDate(activeTasks) : {};

      activeTasks
        .forEach((task) => {
          let taskItem = new Item(null, {});
          taskItem.parameters['workflowTaskSequence'] = task.Sequence;
          this.taskParamItem.items.add(
            task.Sequence + ' ' + task.WorkflowTask.Title,
            taskItem
          );

          var params = this.mapParameters(
            task.WorkflowTask.Params,
            this.taskParamItem.items.get(
              task.Sequence + ' ' + task.WorkflowTask.Title
            ),
            task
          );
          taskParams[task.Sequence + ' ' + task.WorkflowTask.Title] = this.constructRows(params);

        });

      this.workflowTaskProperties = taskParams;
      this.showExecuteWorkflow = true;
      this.dialogStateChanged.emit(true);

      setTimeout(() => {
        this.renderProperties({ ...this.sharedDateProperties, ...this.workflowTaskProperties });
        this.apiSourceParams.forEach((s) => this.loadApiSource(s));
      }, 100);
    } else if (e.columnName == 'Delete') {
      this.selectedExecConfiguration = e;
      this.customerStore.selectWorkflowDefinition(parent.data);
      this.customerStore.selectWorkflowDefinitionExecutionConfiguration(e.row);
      this.showDeleteWFExecConfig = true;
      this.dialogStateChanged.emit(true);

    }
  }

  configChange(e, parent) {
    if (
      e.columnName == 'FK_WorkflowClientExecutionType' &&
      [WorkflowExecutionTypes.Admin, WorkflowExecutionTypes.Manual].includes(
        e.originalEvent
      )
    )
      this.validateMaxAdminAndManualExecutions();
    this.customerStore.selectWorkflowDefinition(parent.data);
    this.customerStore.selectWorkflowDefinitionExecutionConfiguration(e.row);
    this.customerStore.updateCustomerWFDefinitionExecutionConfig(e.row, true);
    if (e.columnName == 'FK_WorkflowClientExecutionType') {
      handleExecutionConfigColumns(e.item, e.originalEvent, parent);
    } else if (e.columnName == 'FunctionType') {
      // Trigger parent item update to notify wf task list
      // component about FunctionType change
      parent.setDataTrigger();
    } else if (e.columnName == 'FK_ScheduleFrequency') {
      e.item.data['ScheduleFrequencyValue'] = null;
      e.item.reValidate();
    } else if (e.columnName == 'MaxAttempts') {
      if (e.item.data['MaxAttempts'] == 1) {
        e.item.data['WaitBetweenAttempts'] = null;
        e.item.reValidate();
      }
    }

    if (
      e.item.data.FK_WorkflowClientExecutionType ==
      WorkflowExecutionTypes.ManualGeneric
      ||
      (
        e.item.data.FK_WorkflowClientExecutionType == WorkflowExecutionTypes.Scheduled
        && e.item.data.CreatedBySystem == true
      )
    ) {
      e.item.acceptChanges();
      e.item.readonly = true;
    }
    this.wizardItem.reCheckDirty();
  }

  validateMaxAdminAndManualExecutions() {}

  configAdd(e, parent) {
    this.customerStore.selectWorkflowDefinition(parent.data);
    if (
      !parent.data.Configurations?.find(
        (c) =>
          c.FK_WorkflowClientExecutionType ==
          WorkflowClientExecutionTypes.MANUALGENERIC
          ||
          (
            c.FK_WorkflowClientExecutionType == WorkflowClientExecutionTypes.SCHEDULED
            && c.CreatedBySystem == true
          )
      )
    ) {
      const colId = this.generateId('executionConfigId');
      const data = {
        PK_ID: colId,
        MaxAttempts: 1,
        FK_TimeZone: this.getClientTimeZone(parent),
        FK_WorkflowClientExecutionType: null,
      };
      this.customerStore.addCustomerWFDefinitionExecutionConfig(data);

      setTimeout(() => {
        this.handleConfigurationActions(parent);
      }, 10);
    }
  }

  getClientTimeZone(item) {
    return item.data.ClientType == ClientTypes.Location
      ? this.customerStore.selected?.TimeZone.ID
      : undefined;
  }

  generateId(name) {
    const colId = +localStorage.getItem(name) - 1;
    localStorage.setItem(name, colId.toString());
    return colId;
  }

  closeExecutionCfg(e) {
    this.showAddExecutionConfiguration = false;
    this.dialogStateChanged.emit(false);
    let taskParams: Record<string, any> = {};
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  mapParameters(params, item: Item, task) {
    const parameters = [];
    this.constructParams(params, parameters, item, task);
    return parameters;
  }

  constructParams(
    params,
    parameters,
    item,
    task,
    parent?: FormProperty,
    readonly?: boolean
  ) {
    params.forEach((param, i) => {
      //minimum columnSpan for run wf dialog
      // if (param.Meta?.ColumnSpan < 4)
      //   param.Meta.ColumnSpan = 4;

      item.data[param.Key] = param.Value;
      const mandatory = param.Meta.Mandatory;
      const validateAllowNull = !mandatory;
      const validationType = mandatory ? ValidationTypes.required : undefined;
      switch (param.Meta.DataType) {
        case 'dayreference':
          const typePlaceholder = param.Value && param.Value.includes(':')
            ? param.Value.split(':')[0]
            : '';
          const valuePlaceholder = param.Value && param.Value.includes(':')
            ? param.Value.split(':')[1]
            : '';

          const ddDate = new FormDateProperty(
            `${task.Sequence}_${param.Key}_wf_date`,
            param.Meta.Title,
            true
          );
          ddDate.validateAllowNull = validateAllowNull;
          ddDate.validate = mandatory;
          ddDate.validationType = validationType;
          ddDate.row = param.Meta.Row;
          ddDate.rowspan = param.Meta.RowSpan;
          ddDate.column = param.Meta.Column;
          ddDate.columnspan = param.Meta.ColumnSpan;

          item.data[`${task.Sequence}_${param.Key}_wf_date`] =
            this.calculateRelativeDate(typePlaceholder, valuePlaceholder);
          applyParamMetaFlags(ddDate, param, readonly);
          if (this.sharedRunDate) {
            // the single date field of the run replaces the per-task date fields
            ddDate.visible = false;
            ddDate.validate = false;
            ddDate.validationType = undefined;
            ddDate.validateAllowNull = true;
          }
          parameters.push(ddDate);

          break;
        case 'Group':
          // a readonly group locks every child it contains
          this.constructParams(
            param.Children,
            parameters,
            item,
            task,
            undefined,
            isParamReadonly(param, readonly)
          );
          break;
        case 'boolean': //boolean
          const pi = new FormInputSwitchProperty(
            `${task.Sequence}_${param.Key}`,
            param.Meta.Title,
            param.Meta.Row,
            param.Meta.Column,
            param.Meta.RowSpan,
            param.Meta.ColumnSpan
          );
          pi.validate = mandatory;
          pi.validationType = validationType;
          pi.validateAllowNull = validateAllowNull;
          pi.parent = parent;
          applyParamMetaFlags(pi, param, readonly);
          parameters.push(pi);
          item.data[`${task.Sequence}_${param.Key}`] = param.Value;
          break;
        case 'CollectionSingle': //collection
          const pd = new FormDropdownProperty(
            `${task.Sequence}_${param.Key}`,
            param.Meta.Title,
            [],
            param.Meta.Row,
            param.Meta.Column,
            param.Meta.RowSpan,
            param.Meta.ColumnSpan
          );

          pd.options = [{ value: '', label: 'Select option' }];
          param.SourceList
            .forEach(x => {
              pd.options.push({ value: x.Value, label: x.Label });
              if ((x?.Children?.length ?? 0) > 0) {
                const childParameters: FormProperty[] = [];
                this.constructParams(
                  x?.Children,
                  childParameters,
                  item,
                  task,
                  pd,
                  isParamReadonly(param, readonly)
                );
                childParameters
                  .forEach((p) => {
                    // a child hidden by its own meta flag stays hidden even when its branch is active
                    p.visible = param.Value == x.Value && p.visible !== false;
                    p.parentKey = x.Value;
                    parameters.push(p);
                  });
              }
            });

          applyParamMetaFlags(pd, param, readonly);
          parameters.push(pd);
          pd.parent = parent;
          pd.validateAllowNull = validateAllowNull;
          pd.validate = mandatory;
          pd.validationType = validationType;
          pd.validationMessage = param.Meta?.ValidationMessage;
          item.data[`${task.Sequence}_${param.Key}`] = param.Value;
          if (param.Meta?.SourceType === 'Api') {
            // the list arrives empty; it is loaded once the parameter it depends on has a value
            pd.readonly = true;
            item.data[`${task.Sequence}_${param.Key}`] = null;
            this.apiSourceParams.push({ property: pd, item, task, param, request: 0 });
          }
          break;
        case 'int':
        case 'string':
        case 'stringarray':
          const tbProp = new FormTextboxProperty(
            `${task.Sequence}_${param.Key}`,
            param.Meta.Title,
            param.Meta.Row,
            param.Meta.Column,
            param.Meta.RowSpan,
            param.Meta.ColumnSpan,
            false
          );

          tbProp.validate = mandatory;
          tbProp.validationType = validationType;
          tbProp.validationMessage = param.Meta?.ValidationMessage;
          if (param.Meta.ValidationRegEx) {
            tbProp.validationRegEx = regExLib[param.Meta.ValidationRegEx];
          }
          tbProp.parent = parent;
          applyParamMetaFlags(tbProp, param, readonly);
          parameters.push(tbProp);
          item.data[`${task.Sequence}_${param.Key}`] = param.Value;
          break;
        case 'emaillist':
          const pEmail = new FormEmailProperty(
            `${task.Sequence}_${param.Key}`,
            param.Meta.Title
          );

          pEmail.validate = mandatory;
          pEmail.validationType = validationType;
          pEmail.column = param.Meta.Column;
          pEmail.row = param.Meta.Row;
          pEmail.columnspan = param.Meta.ColumnSpan;
          pEmail.rowspan = param.Meta.RowSpan;
          pEmail.parent = parent;
          pEmail.validationMessage = param.Meta?.ValidationMessage;
          applyParamMetaFlags(pEmail, param, readonly);
          parameters.push(pEmail);
          item.data[`${task.Sequence}_${param.Key}`] = param.Value;
          break;
        case 'time': {
          const pTime = new FormTimeProperty(`${task.Sequence}_${param.Key}`, param.Meta.Title);
          pTime.validate = mandatory;
          pTime.validateAllowNull = validateAllowNull;
          pTime.validationType = validationType;
          pTime.column = param.Meta.Column;
          pTime.row = param.Meta.Row;
          pTime.columnspan = param.Meta.ColumnSpan;
          pTime.rowspan = param.Meta.RowSpan;
          pTime.parent = parent;
          pTime.rightLabel = param.Meta.RightLabel;
          pTime.validationMessage = param.Meta?.ValidationMessage;
          applyParamMetaFlags(pTime, param, readonly);
          parameters.push(pTime);
          item.data[`${task.Sequence}_${param.Key}`] = param.Value;
          break;
        }
        case 'CollectionMulti':
          const pm = new FormMultiselectProperty(
            `${task.Sequence}_${param.Key}`,
            param.Meta.Title,
            [],
            param.Meta.Row,
            param.Meta.Column,
            param.Meta.RowSpan,
            param.Meta.ColumnSpan,
          );

          pm.options = [];
          param.SourceList
            .forEach(x => {
              pm.options.push({ value: x.Value, label: x.Label });
            });

          pm.parent = parent;
          applyParamMetaFlags(pm, param, readonly);
          parameters.push(pm);
          pm.validate = mandatory;
          pm.validateAllowNull = validateAllowNull;
          pm.validationType = validationType;
          pm.validationMessage = param.Meta?.ValidationMessage;
          item.data[`${task.Sequence}_${param.Key}`] = param.Value;
          break;
        case 'usersbyroles':
          const ubrProp = new FormUsersByRolesProperty();
          ubrProp.name = `${task.Sequence}_${param.Key}`;
          ubrProp.companyGroupCode = (this.customerStore.selected as any).CompanyGroup.Code;
          ubrProp.row = param.Meta.Row;
          ubrProp.rowspan = param.Meta.RowSpan;
          ubrProp.column = param.Meta.Column;
          ubrProp.columnspan = 12;//param.Meta.ColumnSpan;
          ubrProp.label = param.Meta.Title;
          ubrProp.validate = mandatory;
          ubrProp.validateAllowNull = validateAllowNull;
          ubrProp.validationType = validationType;
          ubrProp.validationMessage = param.Meta?.ValidationMessage;
          ubrProp.parent = parent;
          item.data['Roles'] = param.Value[0];
          item.data['Users'] = param.Value[1];
          applyParamMetaFlags(ubrProp, param, readonly);
          parameters.push(ubrProp);
          item.data[`${task.Sequence}_${param.Key}`] = param.Value;
          break;

      }
    });
    parameters.forEach((p) => {
      p.changed.subscribe(this.propChange);
    });
    return parameters;
  }

  private maxRow(properties) {
    if (properties.length > 0)
      return properties.sort((a, b) => b.row - a.row)[0].row;
    return 0;
  }

  private constructRows(properties) {
    let adminExecRows = [];
    if (properties) {
      let maxRow = this.maxRow(properties);
      for (let i = 0; i <= maxRow; i++) {
        let row = new FormRow();
        let filter = properties
          .filter((p) => p.row == i)
          .sort((a, b) => a.column - b.column);
        if (filter.some((p) => p.rowspan)) row.hasRowSpans = true;
        filter.forEach((property) => {
          row.properties.push(property);
        });
        adminExecRows.push(row);
      }
    }
    return adminExecRows;
  }

  calculateRelativeDate(typePlaceholder: any, valuePlaceholder: any): any {
    const now = new Date();

    if (!typePlaceholder || !valuePlaceholder)
      return this.datepipe.transform(now, 'yyyy-MM-dd');

    switch (typePlaceholder) {
      case 'R':
        return this.datepipe.transform(
          now.setDate(now.getDate() + parseInt(valuePlaceholder)),
          'yyyy-MM-dd'
        );
      case 'W':
        return this.datepipe.transform(
          this.getDayOfWeek(now, valuePlaceholder),
          'yyyy-MM-dd'
        );
      case 'M':
        return this.datepipe.transform(
          new Date(
            now.getFullYear(),
            now.getMonth(),
            parseInt(valuePlaceholder)
          ),
          'yyyy-MM-dd'
        );
      default:
        return this.datepipe.transform(now, 'yyyy-MM-dd');
    }
  }

  getDayOfWeek(date: Date, value: string): Date {
    const dayOfWeek = parseInt(value, 10);
    if (isNaN(dayOfWeek)) {
      throw new Error(`Incorrect number provided: ${value}`);
    }
    if (dayOfWeek < 0 || dayOfWeek > 7) {
      throw new Error(`Incorrect day of week value provided: ${dayOfWeek}`);
    }
    // for sunday
    let correctedDayOfWeek = dayOfWeek === 7 ? 0 : dayOfWeek;
    return this.startOfWeek(date, correctedDayOfWeek);
  }

  startOfWeek(dt: Date, startOfWeek: number): Date {
    const currentDayOfWeek = dt.getDay();
    let diff = (7 + (currentDayOfWeek - startOfWeek)) % 7;
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() - diff);
  }

  propChange = (val) => {
    const taskPrefix = val.propertyName.substring(
      0,
      val.propertyName.indexOf('_') + 1
    );

    // parameters declared as radio button lists are mutually exclusive within their group:
    // switching one on switches the others in the same group off
    const radioGroups = [
      ['LoadTips', 'LoadWages', 'LoadCommission', 'LoadWageMakeUp'],
      ['RegularLoad', 'DelayedLoad', 'AutoLoad'],
    ];

    // task parameter definitions store booleans as JSON strings ("True" / "False"),
    // so a switch can report its value as a string instead of a boolean
    const isOn = (v) =>
      v === true || (typeof v === 'string' && v.toLowerCase() === 'true');

    if (isOn(val.newValue)) {
      radioGroups
        .map((group) => group.map((key) => taskPrefix + key))
        .filter((group) => group.indexOf(val.propertyName) > -1)
        .forEach((group) => {
          group
            .filter((name) => name != val.propertyName)
            .forEach((name) => {
              val.item.data[name] = false;
            });
        });
    }
    this.taskParamItem?.items?.values?.forEach((i) => {
      i.reValidate();
    });

    this.apiSourceParams
      .filter((s) => this.dependsOnPropertyName(s) === val.propertyName)
      .forEach((s) => this.loadApiSource(s, val.newValue));
  };

  /**
   * The single date field of the run, prefilled from the BusinessDate value of the first task
   * of the composition (by the composition rules the reopen task, or the report in a report-only run).
   */
  private buildSharedDate(activeTasks: any[]): Record<string, any> {
    const sharedItem = new Item(null, {});
    sharedItem.parameters['sharedRunDate'] = true;
    this.taskParamItem.items.add(this.sharedDateItemKey, sharedItem);

    const firstTask = [...activeTasks].sort((a, b) => a.Sequence - b.Sequence)[0];
    const firstDate: string = findParam(firstTask?.WorkflowTask?.Params, 'BusinessDate')?.Value;
    const [typePlaceholder, valuePlaceholder] =
      firstDate && firstDate.includes(':') ? firstDate.split(':') : ['', ''];

    const prop = new FormDateProperty(
      this.sharedDatePropertyName,
      this.translate.instant('workflow.runBusinessDate'),
      true
    );
    prop.validate = true;
    prop.validationType = ValidationTypes.required;
    prop.validateAllowNull = false;
    prop.row = 0;
    prop.column = 0;
    prop.columnspan = 6;
    prop.changed.subscribe(this.propChange);

    sharedItem.data[this.sharedDatePropertyName] = this.calculateRelativeDate(typePlaceholder, valuePlaceholder);

    return { [this.sharedDateItemKey]: this.constructRows([prop]) };
  }

  /**
   * The name of the form property an Api source depends on: the single date field of the run for a
   * day-reference parameter when the run shares its date, otherwise the parameter of the same task.
   */
  private dependsOnPropertyName(s: ApiSourceParam): string {
    const dependsOn = s.param.Meta?.DependsOn;
    if (!dependsOn) return null;

    const dependsOnParam = findParam(s.task.WorkflowTask.Params, dependsOn);
    if (dependsOnParam?.Meta?.DataType === 'dayreference')
      return this.sharedRunDate
        ? this.sharedDatePropertyName
        : `${s.task.Sequence}_${dependsOn}_wf_date`;

    return `${s.task.Sequence}_${dependsOn}`;
  }

  private dependsOnValue(s: ApiSourceParam) {
    const propertyName = this.dependsOnPropertyName(s);
    if (!propertyName) return null;
    const item = propertyName === this.sharedDatePropertyName
      ? this.taskParamItem.items.get(this.sharedDateItemKey)
      : s.item;
    return item?.data[propertyName];
  }

  /**
   * Loads the list of an Api source parameter and selects its first item; while loading, and while the
   * value it depends on is missing, the field is disabled. An empty list leaves the field empty, so
   * the mandatory parameter prevents the start. A reply to an older request is ignored.
   */
  private loadApiSource(s: ApiSourceParam, dependsOnValue?: any) {
    const name = `${s.task.Sequence}_${s.param.Key}`;
    const date = toIsoDate(dependsOnValue ?? this.dependsOnValue(s), this.datepipe);
    // the change event of the same property can arrive more than once; the list of a date that is
    // already loaded (or loading) is not requested again
    if (s.request > 0 && date === s.date) return;
    s.date = date;
    const request = ++s.request;

    s.property.options = [{ value: '', label: 'Select option' }];
    s.item.data[name] = null;
    s.property.readonly = true;
    s.item.reValidate();

    if (!date || !(this.runWorkflowClientId > 0)) return;

    this.workflowService
      .getApiSourceList(s.param.Meta.Source, this.customerStore.selected.Code, date, this.runWorkflowClientId)
      .subscribe({
        next: (response: any) => {
          if (request !== s.request) return;
          const list: any[] = response?.result ?? [];
          if (list.length) {
            s.property.options = list.map((x) => ({ value: x.Value, label: x.Label }));
            s.item.data[name] = list[0].Value;
          }
          s.property.readonly = isParamReadonly(s.param);
          s.item.reValidate();
        },
        error: () => {
          if (request !== s.request) return;
          s.date = undefined; // a failed load may be retried for the same date
          s.property.readonly = isParamReadonly(s.param);
          s.item.reValidate();
        },
      });
  }

  private subscribe(requestId: string, customerCode: number) {
    this.broadcastService
      .subscribe({
        customerCode,
        event: NotificationEvents.WorkflowExecution_Finished,
        requestId,
      })
      .pipe(first())
      .subscribe((data) => {

        NotificationHelper.handleSignalRResponse(this.translate, data.Message, data.Failed, data.ExecutionStatus);

        this.selectedExecConfiguration.item.parameters.column.Run.disabled =
          false;
        this.selectedExecConfiguration.item.parameters.column.Processing.visible =
          false;
        this.selectedExecConfiguration = null;

        Session.workflowExecutionFinished$.next(true);

        this.unsubscribe(data);
      });
  }

  broadCastOffWithMessage(notificationEvent, message) {
    let requestIds = message.RequestIds;
    if (requestIds)
      requestIds.forEach((requestId) => {
        this.broadcastService.unsubscribe({
          event: notificationEvent,
          requestId,
        });
      });
    else
      this.broadcastService.unsubscribe({
        event: notificationEvent,
      });
  }

  private unsubscribe(message: any) {
    this.broadCastOffWithMessage(
      NotificationEvents.WorkflowExecution_Finished,
      message
    );
  }

  private renderProperties(tasksWithProperties: Record<string, any>) {
    for (var taskName in tasksWithProperties) {
      var rows = tasksWithProperties[taskName];

      rows?.forEach(row => {
        row.properties?.forEach((prop, i) => {
          var placeholder = this.placeholders
            .toArray()
            .find((pc) => pc.reference == prop.name);

          if (placeholder) {
            let inputProviders = [
              prop.component,
              { provide: 'property', useFactory: () => prop },
              { provide: 'textboxProperty', useFactory: () => prop.tb },
              { provide: 'dropdownProperty', useFactory: () => prop.dd },
              {
                provide: 'item',
                useFactory: () => this.taskParamItem.items.get(taskName),
              },
            ];

            let injector = Injector.create(inputProviders);
            let factory = this.resolver.resolveComponentFactory(prop.component);
            let component;
            if (placeholder.viewContainerRef.injector) {
              component = factory.create(
                injector,
                null,
                null,
                placeholder.viewContainerRef.injector.get(NgModuleRef)
              );
            } else {
              component = factory.create(injector, null, null);
            }

            placeholder.viewContainerRef.clear();
            placeholder.viewContainerRef.insert(component.hostView, 0);
            if (component.instance) {
              component.instance.property = prop;
              component.instance.item = this.taskParamItem.items.get(taskName);
              if (prop.numberProperty)
                component.instance.numberProperty = prop.numberProperty;
              if (prop.ddProperty)
                component.instance.dropdownProperty = prop.ddProperty;
              if (prop.ddProperty2)
                component.instance.dropdownProperty2 = prop.ddProperty2;
              if (prop.companyGroupCode)
                component.instance.companyGroupCode = prop.companyGroupCode;
            }
          }
        });
      });

    }
  }

  deleteDefinition(e) {
    this.selectedWFDefinition.data['Deleted'] = true;
    this.selectedWFDefinition.data['Active'] = false;
    this.customerStore.updateCustomerWFDefinition(
      this.selectedWFDefinition.data,
      true
    );
    this.showDeleteWFDefinition = false;
    this.dialogStateChanged.emit(false);
    //this.selectedWFDefinition.readonly = true;
    this.selectedWFDefinition.parameters['Active_readOnly'] = true;
    var cfgItem = this.selectedWFDefinition.items.get('Configurations');
    cfgItem?.items.values.forEach((x) => {
      x.parameters['FK_WorkflowClientExecutionType_readOnly'] = true;
      x.parameters['FunctionType_readOnly'] = true;
      x.parameters['FK_TimeZone_readOnly'] = true;
      x.parameters['TimeWithSpinnersReadOnly'] = true;
      x.parameters['MaxAttempts_readOnly'] = true;
      x.parameters['WaitBetweenAttempts_readOnly'] = true;
      x.parameters['FK_ScheduleFrequency_readOnly'] = true;
      x.parameters['ScheduleFrequencyValue_readOnly'] = true;
      x.parameters['Active_readOnly'] = true;
    });

    this.selectedWFDefinition.updateData(this.selectedWFDefinition.data);
    setTimeout(() => {
      this.selectedWFDefinition.reCheckDirty();
      this.wizardItem.reCheckDirty();
    }, 100);
  }

  closeDeleteDefinition(e) {
    this.showDeleteWFDefinition = false;
     this.dialogStateChanged.emit(false);
  }

  deleteExecConfigration(e) {
    if (!e.item.originalData.PK_ID || e.item.originalData.PK_ID < 0){

    this.customerStore.removeCustomerWFDefinitionExecutionConfig(e.item._data.PK_ID);

    const parentItem = this.wfDefinitions.items.values.find(
      (item) => item.data.PK_ID === this.customerStore.selectedWFDefinition.PK_ID
    );

    if (parentItem) {
      const configurationsItem = parentItem.items.get('Configurations');

      if (configurationsItem) {
        configurationsItem.items.clear();

        const updatedConfigurations = this.customerStore.selectedWFDefinition.Configurations;

        updatedConfigurations.forEach((config) => {
          const configItem = new Item(null, config);
          configurationsItem.items.add(`config_${config.PK_ID}`, configItem);
        });

        parentItem.data.Configurations = updatedConfigurations;

        setTimeout(() => {
          this.handleConfigurationActions(parentItem);
        }, 10);
      }
    }
      this.showDeleteWFExecConfig = false;
      this.dialogStateChanged.emit(false);
    }
    else
    {
    this.selectedExecConfiguration.item.data['Deleted'] = true;
    this.selectedExecConfiguration.item.data['Active'] = false;
    this.customerStore.updateCustomerWFDefinitionExecutionConfig(
      this.selectedExecConfiguration.item.data,
      true
    );
    this.selectedExecConfiguration.item.reCheckDirty();
    this.showDeleteWFExecConfig = false;
     this.dialogStateChanged.emit(false);

    if (this.selectedExecConfiguration.item.data.Deleted == true) {
      this.selectedExecConfiguration.item.parameters[
        'FK_WorkflowClientExecutionType_readOnly'
      ] = true;
      this.selectedExecConfiguration.item.parameters['FunctionType_readOnly'] =
        true;
      this.selectedExecConfiguration.item.parameters['FK_TimeZone_readOnly'] =
        true;
      this.selectedExecConfiguration.item.parameters[
        'TimeWithSpinnersReadOnly'
      ] = true;
      this.selectedExecConfiguration.item.parameters['MaxAttempts_readOnly'] =
        true;
      this.selectedExecConfiguration.item.parameters[
        'WaitBetweenAttempts_readOnly'
      ] = true;
      this.selectedExecConfiguration.item.parameters[
        'FK_ScheduleFrequency_readOnly'
      ] = true;
      this.selectedExecConfiguration.item.parameters[
        'ScheduleFrequencyValue_readOnly'
      ] = true;
      this.selectedExecConfiguration.item.parameters['Active_readOnly'] = true;
    }
  }
  }

  closeDeleteExecConfiguration(e) {
    this.showDeleteWFExecConfig = false;
     this.dialogStateChanged.emit(false);
  }

  changeTaskItem(e) {}

  handleExpandItems(data, action) {
    const pk_id = data.PK_ID.toString();

    const expandItems = this.customerStore?.selected?.Code && localStorage.getItem(this.localStorageKey_expandItems)
      ? JSON.parse(localStorage.getItem(this.localStorageKey_expandItems))
      : [];
    if (action == 'collapse') {
      const index = expandItems.indexOf(pk_id);
      if (index != -1) expandItems.splice(index, 1);
    } else if (action == 'expand') {
      const index = expandItems.indexOf(pk_id);
      if (index == -1) expandItems.push(pk_id);
    }
    localStorage.setItem(
      this.localStorageKey_expandItems,
      JSON.stringify(expandItems)
    );
  }

  updateExpandItems(oldValue, newValue) {
    const expandItems = this.localStorage_expandItems;
    if (!expandItems.length) return;
    const index = expandItems.indexOf(oldValue);
    if (index == -1) return;
    expandItems.splice(index, 1);

    expandItems.push(newValue);
    localStorage.setItem(
      this.localStorageKey_expandItems,
      JSON.stringify(expandItems)
    );
  }

  pageChange(e) {
    if (e?.page == null) return;

    localStorage.setItem(
      `${this.customerStore.selected.Code}_WF_currentPage`,
      e.page.toString()
    );
  }

  addExpandedKey(item) {
    if (!this.expandedRowKeys) this.expandedRowKeys = {};
    this.expandedRowKeys[item.guid.toString()] = true;
  }

  selectWfDefinition(item) {
    this.wfDefinitions?.items.values.forEach((i) => i.unselect());
    item.select();
  }

  onSelectRow(e) {
    this.selectWfDefinition(e);
  }

  focusLastEditedItem(item) {
    if (this.localStorage_expandItems?.length) {
      const index = this.wfDefinitions.items.values.indexOf(item);
      if (index == this.wfDefinitions.items.values.length - 1) {
        const lastId =
          this.localStorage_expandItems[
            this.localStorage_expandItems?.length - 1
          ];
        const lastItem = this.wfDefinitions.items.values.find(
          (x) => x.data.PK_ID == lastId
        );
        if (lastItem) scrollTo(lastItem);
      }
    }
  }

  pageSizeChange(e) {
    this.wizardItem.parameters['pageSize'] = e;
  }
}

export class WorkflowParameters {
  processID: string;
  executionType: number;
  clientCode: number;
  gSClientType: number;
  gSFunctionType: string;
  userCode: number;
  userFullName: string;
  workflowClientExecutionCfgID: number;
  workflowTaskParameters: Array<WorkflowTaskParameters>;
}

export class WorkflowTaskParameters {
  taskSequence: number;
  parameters: { [key: string]: any };
}

/** A task parameter with an Api source on the Run form. */
interface ApiSourceParam {
  property: FormDropdownProperty;
  item: Item;
  task: any;
  param: any;
  /** sequence number of the last request, so that an older reply is ignored */
  request: number;
  /** the date the list was last requested for */
  date?: string;
}

function isTrue(value: any): boolean {
  return value === true || (typeof value === 'string' && value.toLowerCase() === 'true');
}

/** Whether a task declares a parameter shared by the whole run (Meta.SharedAcrossTasks). */
function hasSharedAcrossTasksParam(params: any[]): boolean {
  return (params ?? []).some(
    (p) => isTrue(p?.Meta?.SharedAcrossTasks) || hasSharedAcrossTasksParam(p?.Children)
  );
}

/** A parameter of a task by key, including the children of groups. */
function findParam(params: any[], key: string): any {
  for (const p of params ?? []) {
    if (p?.Key === key) return p;
    const child = findParam(p?.Children, key);
    if (child) return child;
  }
  return undefined;
}

/**
 * A date as "yyyy-MM-dd". A "yyyy-MM-dd..." string keeps its date part as it is (parsing a plain
 * "yyyy-MM-dd" as a Date would move it to the previous day west of UTC); a Date is formatted locally.
 */
function toIsoDate(value: any, datepipe: DatePipe): string {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (match) return match[1];
  }
  return datepipe.transform(value, 'yyyy-MM-dd');
}
